import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { setSentryUser } from './services/sentry';
import LoginPage from './components/LoginPage';
import LazyChunkErrorBoundary from './components/LazyChunkErrorBoundary';
import { initializeFalClient } from './services/fal/config';
import { initializeApiFrameClient } from './services/midjourney/config';
import { supabase } from './services/supabase/client';
import { formatWorkspaceDocumentTitle, workspaceIdentity } from './dev/workspaceIdentity';

const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'));
const APPROVAL_CACHE_TTL_MS = 10 * 60 * 1000;
const approvalChecksByUserId = new Map();

function AppLoadingFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: 'var(--bg-app)',
      }}
    />
  );
}

function getApprovalCacheKey(userId) {
  return `glowghost:approval:${userId}`;
}

function readApprovalCacheRecord(userId) {
  if (!userId) return null;

  try {
    const cachedValue = window.localStorage.getItem(getApprovalCacheKey(userId));
    if (cachedValue === 'true') {
      return { value: true, checkedAt: 0 };
    }
    if (!cachedValue) return null;

    const parsed = JSON.parse(cachedValue);
    if (parsed?.value === true && Number.isFinite(parsed.checkedAt)) {
      return { value: true, checkedAt: parsed.checkedAt };
    }
  } catch {
    // Ignore storage failures and fall back to the live check.
  }

  return null;
}

function readCachedApproval(userId) {
  return readApprovalCacheRecord(userId)?.value ?? null;
}

function hasFreshCachedApproval(userId) {
  const cached = readApprovalCacheRecord(userId);
  if (cached?.value !== true || !cached.checkedAt) return false;
  return Date.now() - cached.checkedAt < APPROVAL_CACHE_TTL_MS;
}

function writeApprovalCache(userId, isApproved) {
  if (!userId) return;

  try {
    if (isApproved === true) {
      window.localStorage.setItem(getApprovalCacheKey(userId), JSON.stringify({
        value: true,
        checkedAt: Date.now(),
      }));
    } else {
      window.localStorage.removeItem(getApprovalCacheKey(userId));
    }
  } catch {
    // Ignore storage failures.
  }
}

function normalizeApprovalValue(data) {
  if (typeof data?.is_approved !== 'boolean') return null;
  return data.is_approved;
}

function isMissingProfileError(error) {
  return error?.code === 'PGRST116';
}

function wait(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

async function queryApproval(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_approved')
    .eq('id', userId)
    .single();

  const value = normalizeApprovalValue(data);
  if (!error && value !== null) {
    return { value, error: null };
  }

  return { value: null, error };
}

function getApprovalCheck(userId, { retryOnFailure }) {
  const existing = approvalChecksByUserId.get(userId);
  if (existing) return existing;

  const promise = (async () => {
    const firstResult = await queryApproval(userId);
    if (firstResult.value !== null) return firstResult;
    if (!retryOnFailure) return firstResult;

    await wait(1500);
    const retryResult = await queryApproval(userId);
    return retryResult.value !== null ? retryResult : {
      value: null,
      error: retryResult.error || firstResult.error,
    };
  })().finally(() => {
    if (approvalChecksByUserId.get(userId) === promise) {
      approvalChecksByUserId.delete(userId);
    }
  });

  approvalChecksByUserId.set(userId, promise);
  return promise;
}

function App() {
  const { user, isLoading, getAccessToken } = useAuth();
  const userId = user?.id ?? null;
  const [approvalRecord, setApprovalRecord] = useState({
    userId: null,
    value: !supabase ? true : null,
  });

  useEffect(() => {
    if (user) {
      initializeFalClient(getAccessToken);
      initializeApiFrameClient(getAccessToken);
    }
  }, [user, getAccessToken]);

  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  const isApproved = useMemo(() => {
    if (!supabase) return true;
    if (!userId) return null;
    if (approvalRecord.userId === userId) return approvalRecord.value;
    return readCachedApproval(userId);
  }, [approvalRecord, userId]);

  const shouldUseWorkspaceBaseTitle = isLoading || !user || isApproved !== true;

  useEffect(() => {
    if (!userId || !supabase) return;

    let cancelled = false;

    const applyApprovalValue = (nextValue) => {
      setApprovalRecord({ userId, value: nextValue });
      writeApprovalCache(userId, nextValue);
    };

    const handleApprovalFailure = (error) => {
      if (isMissingProfileError(error)) {
        applyApprovalValue(false);
        return;
      }

      const cachedValue = readCachedApproval(userId);
      if (cachedValue === true) {
        setApprovalRecord({ userId, value: true });
        return;
      }

      console.warn('[auth] approval check failed:', error?.message || 'No approval record returned');
      setApprovalRecord({ userId, value: null });
    };

    const checkApproval = async () => {
      if (hasFreshCachedApproval(userId)) {
        setApprovalRecord({ userId, value: true });
        return;
      }

      const hasCachedApproval = readCachedApproval(userId) === true;
      if (hasCachedApproval) {
        setApprovalRecord({ userId, value: true });
      }

      const result = await getApprovalCheck(userId, { retryOnFailure: !hasCachedApproval });
      if (cancelled) return;

      if (result.value !== null) {
        applyApprovalValue(result.value);
        return;
      }

      handleApprovalFailure(result.error);
    };

    checkApproval().catch(error => {
      if (!cancelled) {
        handleApprovalFailure(error);
      }
    });

    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!shouldUseWorkspaceBaseTitle) return;

    document.title = formatWorkspaceDocumentTitle('Modul', workspaceIdentity);
  }, [shouldUseWorkspaceBaseTitle]);

  if (isLoading || (user && isApproved === null)) {
    return <AppLoadingFallback />;
  }

  if (!user) {
    return <LoginPage />;
  }

  if (!isApproved) {
    return <LoginPage mode="pending" />;
  }

  return (
    <LazyChunkErrorBoundary section="authenticated-app">
      <Suspense fallback={<AppLoadingFallback />}>
        <AuthenticatedApp />
      </Suspense>
    </LazyChunkErrorBoundary>
  );
}

export default App;
